import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  PieChart, 
  Pie, 
  Cell,
  Legend
} from 'recharts';
import { 
  Users, 
  Activity, 
  AlertTriangle, 
  Clock, 
  UserPlus,
  ArrowRight,
  TrendingUp,
  CalendarDays,
  User,
  BarChart3,
  PieChart as PieChartIcon,
  Calendar,
  Filter,
  Settings,
  Pencil,
  Trash2,
  Plus,
  Loader2,
  Package
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  formatDate, 
  PRIORITY_LABELS, 
  PRIORITY_COLORS,
  ORIGEM_LABELS,
  ORIGEM_COLORS,
  LOS_BUCKET_LABELS,
  LOS_BUCKET_COLORS,
  todayLocal
} from '../lib/utils';

// Chart colors for each distribution type
const EQUIPE_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];
const LOS_COLORS = {
  '0_5': '#10B981',
  '6_15': '#3B82F6',
  '16_30': '#F59E0B',
  '30_plus': '#EF4444'
};

// StatCard component moved outside Dashboard
const StatCard = ({ title, value, icon: Icon, description, color, testId, onClick }) => (
  <Card 
    className={`hover:shadow-md transition-shadow duration-200 ${onClick ? 'cursor-pointer' : ''}`} 
    data-testid={testId}
    onClick={onClick}
  >
    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="h-4 w-4" />
      </div>
    </CardHeader>
    <CardContent>
      <div className="text-3xl font-bold font-['Manrope']">{value}</div>
      {description && (
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      )}
    </CardContent>
  </Card>
);

// TarefaItem component moved outside Dashboard
const TarefaItem = ({ tarefa, isOverdue, internacao, onTarefaClick }) => {
  return (
    <div 
      className={`
        p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors
        ${isOverdue ? 'border-l-4 border-l-red-500' : ''}
        ${tarefa.prioridade === 'alta' ? 'border-l-4 border-l-red-500' : ''}
        ${tarefa.prioridade === 'media' && !isOverdue ? 'border-l-4 border-l-yellow-500' : ''}
      `}
      onClick={() => onTarefaClick(tarefa)}
      data-testid={`dashboard-tarefa-${tarefa.id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{tarefa.titulo}</p>
          {internacao && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              <User className="h-3 w-3 inline mr-1" />
              {internacao.paciente_nome}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Badge 
            variant="outline" 
            className={`text-xs ${PRIORITY_COLORS[tarefa.prioridade]?.split(' ').slice(0,2).join(' ')}`}
          >
            {PRIORITY_LABELS[tarefa.prioridade]}
          </Badge>
          {isOverdue && (
            <Badge variant="destructive" className="text-xs">
              Vencida
            </Badge>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
        <CalendarDays className="h-3 w-3" />
        {formatDate(tarefa.data_prevista)}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { api, can } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pa, setPa] = useState(null);
  useEffect(() => { api.get('/primeiros-atendimentos/dashboard').then((r) => setPa(r.data)).catch(() => {}); }, [api]);
  const [tarefasHoje, setTarefasHoje] = useState([]);
  const [tarefasVencidas, setTarefasVencidas] = useState([]);
  const [internacoes, setInternacoes] = useState({});
  const [internacoesLista, setInternacoesLista] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Distribution selector state
  const [distributionView, setDistributionView] = useState('equipe');
  const [distributionData, setDistributionData] = useState(null);
  const [loadingDistribution, setLoadingDistribution] = useState(false);
  
  // Detail modal states
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailTitle, setDetailTitle] = useState('');
  
  // Equipes data
  const [equipes, setEquipes] = useState([]);
  
  // Equipe editing state
  const [manageEquipesModalOpen, setManageEquipesModalOpen] = useState(false);
  const [equipeModalOpen, setEquipeModalOpen] = useState(false);
  const [editingEquipe, setEditingEquipe] = useState(null);
  const [equipeForm, setEquipeForm] = useState({ nome: '', capacidade: 7, cor: '#3B82F6' });
  const [savingEquipe, setSavingEquipe] = useState(false);
  
  // Estoque alerts state
  const [estoqueAlertas, setEstoqueAlertas] = useState({ critico: 0, moderado: 0, satisfatorio: 0, itens_criticos: [] });
  
  const colorOptions = [
    { value: '#3B82F6', name: 'Azul' },
    { value: '#10B981', name: 'Verde' },
    { value: '#F59E0B', name: 'Amarelo' },
    { value: '#EF4444', name: 'Vermelho' },
    { value: '#8B5CF6', name: 'Roxo' },
    { value: '#EC4899', name: 'Rosa' },
    { value: '#14B8A6', name: 'Teal' },
    { value: '#F97316', name: 'Laranja' },
  ];

  const today = todayLocal();

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchDistributionData(distributionView);
    }
  }, [distributionView, loading]);

  const fetchData = async () => {
    try {
      const [statsRes, tarefasRes, internacoesRes, equipesRes, estoqueRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/tarefas'),
        api.get('/internacoes?ativas=true'),
        api.get('/equipes'),
        api.get('/estoque/dashboard').catch(() => ({ data: { alertas: { critico: 0, moderado: 0, satisfatorio: 0 }, itens_criticos: [] } }))
      ]);
      
      setStats(statsRes.data);
      setEquipes(equipesRes.data.equipes || []);
      setEstoqueAlertas({
        critico: estoqueRes.data?.alertas?.critico || 0,
        moderado: estoqueRes.data?.alertas?.moderado || 0,
        satisfatorio: estoqueRes.data?.alertas?.satisfatorio || 0,
        itens_criticos: estoqueRes.data?.itens_criticos || []
      });
      
      const intList = internacoesRes.data;
      setInternacoesLista(intList);
      
      const intMap = {};
      intList.forEach(i => {
        intMap[i.id] = i;
      });
      setInternacoes(intMap);
      
      const allTarefas = tarefasRes.data;
      setTarefasHoje(allTarefas.filter(t => 
        t.data_prevista === today && 
        t.status !== 'concluida' && 
        t.status !== 'cancelada'
      ).slice(0, 5));
      
      setTarefasVencidas(allTarefas.filter(t => 
        t.data_prevista < today && 
        t.status !== 'concluida' && 
        t.status !== 'cancelada'
      ).slice(0, 5));
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDistributionData = async (view) => {
    setLoadingDistribution(true);
    try {
      if (view === 'equipe') {
        // Group by equipe
        const data = equipes.map(eq => ({
          id: eq.id,
          name: eq.nome,
          count: eq.total_pacientes || 0,
          color: eq.cor,
          patients: internacoesLista.filter(i => (i.equipe_id || 'sem_equipe') === eq.id)
        }));
        setDistributionData(data);
      } else if (view === 'origem') {
        // Group by origem_grupo
        const origemMap = {};
        internacoesLista.forEach(i => {
          const grupo = i.origem_grupo || 'HGR';
          if (!origemMap[grupo]) {
            origemMap[grupo] = { id: grupo, name: grupo, count: 0, patients: [] };
          }
          origemMap[grupo].count++;
          origemMap[grupo].patients.push(i);
        });
        setDistributionData(Object.values(origemMap));
      } else if (view === 'tempo') {
        // Group by LOS buckets
        const buckets = {
          '0_5': { id: '0_5', name: '0-5 dias', count: 0, patients: [] },
          '6_15': { id: '6_15', name: '6-15 dias', count: 0, patients: [] },
          '16_30': { id: '16_30', name: '16-30 dias', count: 0, patients: [] },
          '30_plus': { id: '30_plus', name: '> 30 dias', count: 0, patients: [] }
        };
        
        const todayDate = new Date();
        internacoesLista.forEach(i => {
          const dataInt = new Date(i.data_internacao);
          const dias = Math.floor((todayDate - dataInt) / (1000 * 60 * 60 * 24));
          
          let bucket = '30_plus';
          if (dias <= 5) bucket = '0_5';
          else if (dias <= 15) bucket = '6_15';
          else if (dias <= 30) bucket = '16_30';
          
          buckets[bucket].count++;
          buckets[bucket].patients.push({ ...i, dias_internacao: dias });
        });
        
        setDistributionData(Object.values(buckets));
      }
    } catch (error) {
      console.error('Error fetching distribution:', error);
    } finally {
      setLoadingDistribution(false);
    }
  };

  const handleBarClick = (data) => {
    if (data && data.patients && data.patients.length > 0) {
      setDetailData(data.patients);
      setDetailTitle(`${data.name} (${data.count} paciente${data.count !== 1 ? 's' : ''})`);
      setDetailModalOpen(true);
    }
  };

  const handleTarefaClick = (tarefa) => {
    const internacao = internacoes[tarefa.internacao_id];
    if (internacao) {
      navigate(`/pacientes/${internacao.paciente_id}/internacao/${tarefa.internacao_id}`);
    }
  };

  const handlePatientClick = (paciente_id, internacao_id) => {
    navigate(`/pacientes/${paciente_id}/internacao/${internacao_id}`);
  };

  // Equipe management functions
  const handleOpenEquipeModal = (equipe) => {
    setEditingEquipe(equipe);
    setEquipeForm({
      nome: equipe.nome,
      capacidade: equipe.capacidade || 7,
      cor: equipe.cor || '#3B82F6'
    });
    setEquipeModalOpen(true);
  };

  const handleSaveEquipe = async () => {
    if (!equipeForm.nome.trim()) {
      return;
    }

    setSavingEquipe(true);
    try {
      await api.put(`/equipes/${editingEquipe.id}`, equipeForm);
      setEquipeModalOpen(false);
      fetchData();
    } catch (err) {
      console.error('Error saving equipe:', err);
    } finally {
      setSavingEquipe(false);
    }
  };

  const handleAddEquipe = async () => {
    const newName = `Equipe ${(equipes?.length || 0) + 1}`;
    try {
      await api.post('/equipes', { nome: newName, capacidade: 7, cor: colorOptions[(equipes?.length || 0) % colorOptions.length].value });
      fetchData();
    } catch (err) {
      console.error('Error adding equipe:', err);
    }
  };

  const handleDeleteEquipe = async (equipeId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta equipe?')) return;
    try {
      await api.delete(`/equipes/${equipeId}`);
      fetchData();
    } catch (err) {
      console.error('Error deleting equipe:', err);
    }
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground mt-1">
              Visão geral das internações domiciliares
            </p>
          </div>
          {can('criar_pacientes') && (
          <Link to="/pacientes/novo">
            <Button className="gap-2" data-testid="btn-novo-paciente-dashboard">
              <UserPlus className="h-4 w-4" />
              Novo Paciente
            </Button>
          </Link>
          )}
        </div>

        {pa && (
          <Card data-testid="pa-dashboard">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center justify-between">ACOMPANHAMENTO DAS PRIMEIRAS AVALIAÇÕES<Link to="/primeiros-atendimentos" className="text-xs font-normal text-primary hover:underline" data-testid="pa-dashboard-link">Abrir painel</Link></CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[['novas_admissoes', 'Novas admissões', ''], ['pacientes_sem_primeiro_atendimento_completo', 'Sem 1ª avaliação completa', 'text-amber-700'], ['aguardando_agendamento', 'Aguardando agendamento', 'text-slate-700'], ['visitas_agendadas', 'Avaliações agendadas', 'text-blue-700'],
                ['realizados', 'Primeiras avaliações realizadas', 'text-emerald-700'], ['atrasados', 'Avaliações atrasadas', 'text-rose-700'], ['nao_indicados', 'Especialidades não indicadas', 'text-gray-600'], ['acompanhamento_concluido', 'Acompanhamento inicial concluído', 'text-emerald-800'],
                ['guias_primeiro_atendimento_pendentes', 'Guias da 1ª avaliação pendentes', 'text-amber-700'], ['aguardando_decisao', 'Aguardando decisão do especialista', 'text-violet-700'],
                ['acompanhamento_indicado', 'Acompanhamento indicado · a atribuir', 'text-cyan-700'], ['atribuidos', 'Pacientes atribuídos', 'text-emerald-800']].map(([k, l, c]) => (
                <div key={k} className="rounded-lg border bg-muted/30 p-3" data-testid={`pa-card-${k}`}><p className="text-xs text-muted-foreground">{l}</p><p className={`text-2xl font-semibold ${c}`}>{pa[k] ?? 0}</p></div>))}
            </CardContent>
          </Card>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {loading ? (
            <>
              {[1, 2, 3, 4, 5].map((i) => (
                <Card key={i}>
                  <CardHeader className="pb-2">
                    <Skeleton className="h-4 w-24" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-8 w-16" />
                  </CardContent>
                </Card>
              ))}
            </>
          ) : (
            <>
              <StatCard
                title="Total Internados"
                value={stats?.total_internados || 0}
                icon={Users}
                description="Pacientes ativos"
                color="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400"
                testId="stat-total-internados"
                onClick={() => navigate('/kanban')}
              />
              <StatCard
                title="Dias de Internação"
                value={`${stats?.tempo_medio_internacao || 0}d`}
                icon={Clock}
                description="Média de permanência"
                color="bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400"
                testId="stat-dias-internacao"
                onClick={() => setDistributionView('tempo')}
              />
              <StatCard
                title="Tarefas Vencidas"
                value={stats?.tarefas_vencidas || 0}
                icon={AlertTriangle}
                description="Requerem atenção"
                color="bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
                testId="stat-tarefas-vencidas"
                onClick={() => navigate('/tarefas')}
              />
              <StatCard
                title="Em Atenção"
                value={stats?.por_status?.ativo_atencao || 0}
                icon={Activity}
                description="Intercorrências"
                color="bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400"
                testId="stat-em-atencao"
                onClick={() => navigate('/kanban')}
              />
            </>
          )}
        </div>

        {/* Tasks Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Tarefas do Dia */}
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-blue-600" />
                <CardTitle className="text-lg font-['Manrope']">Tarefas do Dia</CardTitle>
              </div>
              <Link to="/tarefas">
                <Button variant="ghost" size="sm">
                  Ver todas
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : tarefasHoje.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  Nenhuma tarefa para hoje
                </div>
              ) : (
                <div className="space-y-2">
                  {tarefasHoje.map(tarefa => (
                    <TarefaItem 
                      key={tarefa.id} 
                      tarefa={tarefa} 
                      isOverdue={false} 
                      internacao={internacoes[tarefa.internacao_id]}
                      onTarefaClick={handleTarefaClick}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tarefas Vencidas */}
          <Card className={tarefasVencidas.length > 0 ? 'border-l-4 border-l-red-500' : ''}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-2">
                <AlertTriangle className={`h-5 w-5 ${tarefasVencidas.length > 0 ? 'text-red-600' : 'text-muted-foreground'}`} />
                <CardTitle className="text-lg font-['Manrope']">Tarefas Vencidas</CardTitle>
                {tarefasVencidas.length > 0 && (
                  <Badge variant="destructive">{tarefasVencidas.length}</Badge>
                )}
              </div>
              <Link to="/tarefas">
                <Button variant="ghost" size="sm">
                  Ver todas
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
                </div>
              ) : tarefasVencidas.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground">
                  Nenhuma tarefa vencida
                </div>
              ) : (
                <div className="space-y-2">
                  {tarefasVencidas.map(tarefa => (
                    <TarefaItem 
                      key={tarefa.id} 
                      tarefa={tarefa} 
                      isOverdue={true} 
                      internacao={internacoes[tarefa.internacao_id]}
                      onTarefaClick={handleTarefaClick}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Alertas de Estoque Card */}
        {(estoqueAlertas.critico > 0 || estoqueAlertas.moderado > 0) && (
          <Card className="border-l-4 border-l-red-500" data-testid="estoque-alertas-card">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-red-600" />
                <CardTitle className="text-lg font-['Manrope']">Alertas de Estoque</CardTitle>
              </div>
              <Link to="/estoque?tab=alertas">
                <Button variant="ghost" size="sm">
                  Ver todos
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-sm">Crítico: <strong>{estoqueAlertas.critico}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <span className="text-sm">Moderado: <strong>{estoqueAlertas.moderado}</strong></span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-sm">Satisfatório: <strong>{estoqueAlertas.satisfatorio}</strong></span>
                </div>
              </div>
              {estoqueAlertas.itens_criticos.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground mb-2">Itens críticos (≤7 dias de cobertura):</p>
                  {estoqueAlertas.itens_criticos.map(item => (
                    <div key={item.item_id} className="flex justify-between items-center p-2 bg-red-50 dark:bg-red-900/20 rounded-md">
                      <span className="font-medium text-sm">{item.item_nome}</span>
                      <div className="text-right">
                        <span className="text-sm text-muted-foreground">
                          {item.estoque_atual} {item.unidade}
                        </span>
                        <Badge className="ml-2 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                          {item.dias_cobertura ? `${item.dias_cobertura} dias` : 'Crítico'}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Distribution Section with Selector */}
        <Card data-testid="distribution-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Distribuição de Pacientes
              </CardTitle>
              <div className="flex items-center gap-2">
                {can('gerenciar_equipes') && distributionView === 'equipe' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setManageEquipesModalOpen(true)}
                    className="gap-2"
                  >
                    <Settings className="h-4 w-4" />
                    Gerenciar Equipes
                  </Button>
                )}
                <Select value={distributionView} onValueChange={setDistributionView}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="equipe">Por Equipe</SelectItem>
                  <SelectItem value="origem">Por Origem</SelectItem>
                  <SelectItem value="tempo">Por Tempo de Internação</SelectItem>
                </SelectContent>
              </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading || loadingDistribution ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : distributionData && distributionData.length > 0 ? (
              <div className="space-y-3">
                {distributionData.map((item, index) => {
                  const total = stats?.total_internados || 1;
                  const percentage = Math.round((item.count / total) * 100) || 0;
                  
                  // Get color based on view type
                  let barColor = EQUIPE_COLORS[index % EQUIPE_COLORS.length];
                  if (distributionView === 'equipe' && item.color) {
                    barColor = item.color;
                  } else if (distributionView === 'origem') {
                    // Map origem IDs to colors
                    const origemColorMap = {
                      HGR: '#3B82F6',
                      HMI: '#F59E0B',
                      PACS: '#06B6D4',
                      cardiologia: '#EF4444',
                      paliativo: '#8B5CF6',
                      clinico: '#3B82F6',
                      cirurgia: '#F97316',
                      completar_atb: '#22C55E',
                    };
                    barColor = origemColorMap[item.id] || '#6B7280';
                  } else if (distributionView === 'tempo') {
                    barColor = LOS_COLORS[item.id] || '#6B7280';
                  }
                  
                  return (
                    <div 
                      key={item.id} 
                      className="flex items-center gap-4 cursor-pointer hover:bg-muted/50 rounded-lg p-2 -mx-2 transition-colors"
                      onClick={() => handleBarClick(item)}
                      title={`Clique para ver ${item.count} paciente(s)`}
                    >
                      <div className="w-32 text-sm font-medium truncate flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: barColor }}
                        />
                        {item.name}
                      </div>
                      <div className="flex-1 h-8 bg-muted rounded-full overflow-hidden">
                        <div 
                          className="h-full transition-all duration-500"
                          style={{ width: `${percentage}%`, backgroundColor: barColor }}
                        />
                      </div>
                      <div className="w-16 text-right">
                        <span className="font-semibold">{item.count}</span>
                        <span className="text-muted-foreground text-sm ml-1">({percentage}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                Nenhum dado disponível
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="hover:shadow-md transition-shadow cursor-pointer group" data-testid="card-ir-kanban">
            <Link to="/kanban">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg font-['Manrope']">Quadro Kanban</h3>
                  <p className="text-sm text-muted-foreground">
                    Visualize e gerencie as internações
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
              </CardContent>
            </Link>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer group" data-testid="card-ir-lista">
            <Link to="/lista">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg font-['Manrope']">Lista de Internações</h3>
                  <p className="text-sm text-muted-foreground">
                    Visão tabular completa
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
              </CardContent>
            </Link>
          </Card>

          <Card className="hover:shadow-md transition-shadow cursor-pointer group" data-testid="card-ir-mapa">
            <Link to="/mapa">
              <CardContent className="p-6 flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-lg font-['Manrope']">Mostrar no Mapa</h3>
                  <p className="text-sm text-muted-foreground">
                    Localização e itinerários
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
              </CardContent>
            </Link>
          </Card>
        </div>
      </div>

      {/* Detail Modal for Distribution Click-through */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {detailTitle}
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto">
            {detailData && detailData.length > 0 ? (
              <div className="space-y-2">
                {detailData.map((paciente) => (
                  <div 
                    key={paciente.id}
                    className="p-3 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors"
                    onClick={() => {
                      setDetailModalOpen(false);
                      navigate(`/pacientes/${paciente.paciente_id}/internacao/${paciente.id}`);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <User className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{paciente.paciente_nome || 'Paciente'}</p>
                          <p className="text-sm text-muted-foreground">
                            Internado em {formatDate(paciente.data_internacao)}
                            {paciente.dias_internacao !== undefined && (
                              <Badge variant="outline" className="ml-2">
                                D+{paciente.dias_internacao}
                              </Badge>
                            )}
                          </p>
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                    {paciente.diagnostico_principal && (
                      <p className="text-xs text-muted-foreground mt-1 ml-8">
                        {paciente.diagnostico_principal}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum paciente nesta categoria
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Equipes Modal */}
      <Dialog open={manageEquipesModalOpen} onOpenChange={setManageEquipesModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Gerenciar Equipes
            </DialogTitle>
            <DialogDescription>
              Edite nome, capacidade e cor das equipes. Clique em uma equipe para editar.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {equipes.map((equipe) => (
              <div 
                key={equipe.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: equipe.cor || '#3B82F6' }}
                  />
                  <div>
                    <div className="font-medium">{equipe.nome}</div>
                    <div className="text-sm text-muted-foreground">
                      Capacidade: {equipe.capacidade || 7} pacientes
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">
                    {equipe.total_pacientes || 0}/{equipe.capacidade || 7}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleOpenEquipeModal(equipe)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive"
                    onClick={() => handleDeleteEquipe(equipe.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            
            <Button
              variant="outline"
              className="w-full gap-2"
              onClick={handleAddEquipe}
            >
              <Plus className="h-4 w-4" />
              Adicionar Nova Equipe
            </Button>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setManageEquipesModalOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Single Equipe Modal */}
      <Dialog open={equipeModalOpen} onOpenChange={setEquipeModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Editar Equipe
            </DialogTitle>
            <DialogDescription>
              Altere o nome, capacidade e cor da equipe
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome da Equipe</Label>
              <Input
                value={equipeForm.nome}
                onChange={(e) => setEquipeForm({ ...equipeForm, nome: e.target.value })}
                placeholder="Ex: Equipe Norte"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Capacidade (pacientes)</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={equipeForm.capacidade}
                onChange={(e) => setEquipeForm({ ...equipeForm, capacidade: parseInt(e.target.value) || 7 })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      equipeForm.cor === color.value ? 'border-foreground scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color.value }}
                    onClick={() => setEquipeForm({ ...equipeForm, cor: color.value })}
                    title={color.name}
                  />
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEquipeModalOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSaveEquipe} disabled={savingEquipe}>
              {savingEquipe ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Dashboard;
